import { XMarkIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Animate } from 'react-simple-animate';
import { addNonMotorPolicyUpdate, addPolicyUpdate, deleteNonMotorPolicyUpdate, deletePolicyUpdate, editNonMotorPolicyUpdate, editPolicyUpdate } from '../../../../api/requests';
import Button from '../../../../components/form-control/Button';
import TextArea from '../../../../components/form-control/TextArea';
import useRoute from '../../../../hooks/routesHook';
import { IPolicyUpdate } from '../../../../models/policy/IPolicy';
import { Policy } from '../../../../models/policy/Policy';
import { removeUnderscoresAndCapitalize, showToast } from '../../../../utils/miscFunctions';
import useUserAccount from '../../../../hooks/userAccountHook';
import useLayoutHook from '../../../../hooks/layoutHook';
import { IRequestResponse } from '../../../../resources/interfaces';
import { Calendar, Clock, Edit2, MoreVertical, Pin, StickyNote, Tag, Trash2, User, X } from 'lucide-react';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import { getCategoryColor, getPriorityColor } from '../../../../resources/constants';
import NonAutoPolicy from '../../../../models/nonAutoPolicy/NonAutoPolicy';

interface IPolicyUpdates {
  onProceed: () => void;
  onClose: () => void;
  show: boolean;
  updates: IPolicyUpdate[];
  policyId: string;
  onPolicyUpdated?: (policy: Policy) => void;
  onNonMotorPolicyUpdated?: (policy: NonAutoPolicy) => void;
  isNonMotor?: boolean;
}

const PolicyUpdates = ({
  // onProceed,
  onClose,
  show = false,
  updates,
  policyId,
  onPolicyUpdated,
  onNonMotorPolicyUpdated,
  isNonMotor = false
}: IPolicyUpdates) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [reply, setReply] = useState<string>('');
  const {screenWidth} = useLayoutHook()
  const { user } = useUserAccount();
  const { lockBackground, unlockBackground } = useRoute();
  const sectionRef = useRef<HTMLDivElement>(null);
  const topSection = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeNoteMenu, setActiveNoteMenu] = useState<string | null>(null);
  const [noteTags, setNoteTags] = useState<string[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteCategory, setNoteCategory] = useState<string | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  const totalPinnedUpdates = updates?.filter((update:IPolicyUpdate) => update.pin === true).length;
  const CATEGORIES:string[] = user.isInternalAdmin() ? 
  [ "CLAIM", "COMMISSION", "COMMUNITY", "CUSTOMER", "INSURER", "LEAD", "LOAN", "PAYMENT", "PAYOUT", "POLICY", "PREMIUM_FINANCING", "QUOTE", "STAFF", "TASK", "TEAM", "VEHICLE", "VEHICLE_INSPECTION" ]
  :  [ "CUSTOMER", "INSURER", "POLICY", "QUOTE", "VEHICLE", "VEHICLE_INSPECTION" ]
  const PRIORITIES:string[] = [ "URGENT", "MID", "LOW" ];

  const filteredNotes = updates.sort((a, b) => {
      if (a.pin && !b.pin) return -1;
      if (!a.pin && b.pin) return 1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    }) || [];

  useEffect(() => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    if (show) {
      lockBackground();
    } else {
      unlockBackground();
    }
  }, [show]);

  const endStyles = {
    zIndex: 2147483647,
    position: 'absolute' as 'absolute',
    top: 65,
    right: screenWidth > 550 ? 0 : '20px',
    width: screenWidth > 550 ? '681px' : '100vw',
    minHeight: 'calc(100vh - 105px)'
  };

  const startStyles = {
    zIndex: 2147483647,
    position: 'absolute' as 'absolute',
    top: 65,
    right: screenWidth > 550 ? '-681px' : '-100vw',
    width: screenWidth > 550 ? '681px' : '100vw',
    minHeight: 'calc(100vh - 105px)'
  };

  const handleSubmit = () => {
    if (!reply) {
      showToast.error('You need to provide a message to proceed.');
      return;
    }
    const payload = {
      category: noteCategory ? String(noteCategory) : null, 
      description: reply, 
      pin: false, 
      priority: priority ? priority === 'MID' ? String('NORMAL') : String(priority) : null
    }
    if(editingNoteId){
       if(isNonMotor){
      editNonMotorPolicyUpdate(policyId, Number(editingNoteId), payload)
      .then((response: IRequestResponse) => {
        if (response) {
          onNonMotorPolicyUpdated?.(new NonAutoPolicy(response?.data));
          setReply('');
        }
      })
      .finally(() => setSubmitting(false));
    }

    if(!isNonMotor){
      editPolicyUpdate(policyId, Number(editingNoteId), payload)
      .then((response: IRequestResponse) => {
        if (response) {
          onPolicyUpdated?.(new Policy(response?.data));
          setReply('');
        }
      })
      .finally(() => setSubmitting(false));
    }
    }
    setSubmitting(true);

    if(!editingNoteId){
       if(isNonMotor){
         addNonMotorPolicyUpdate(policyId, payload)
        .then((response: IRequestResponse) => {
          if (response) {
            onNonMotorPolicyUpdated?.(new NonAutoPolicy(response?.data));
            setReply('');
          }
        })
        .finally(() => setSubmitting(false))
      } 
 
      if(!isNonMotor){
    
      addPolicyUpdate(policyId, payload)
        .then((response: IRequestResponse) => {
          if (response) {
            onPolicyUpdated?.(new Policy(response?.data));
            setReply('');
          }
        })
        .finally(() => setSubmitting(false));
      } 
    }
   

  };

   const handleUpdatePin = (id:number, update: IPolicyUpdate, pin: boolean) => {

    const payload = {
      category: update.category, 
      description: update.description, 
      pin: pin, 
      priority: update.priority
    }
    setSubmitting(true);

     if(isNonMotor){
      editNonMotorPolicyUpdate(policyId, id, payload)
      .then((response: IRequestResponse) => {
        if (response) {
          onNonMotorPolicyUpdated?.(new NonAutoPolicy(response?.data));
          setReply('');
        }
      })
      .finally(() => setSubmitting(false));
    }

    if(!isNonMotor){
      editPolicyUpdate(policyId, id, payload)
      .then((response: IRequestResponse) => {
        if (response) {
          onPolicyUpdated?.(new Policy(response?.data));
          setReply('');
        }
      })
      .finally(() => setSubmitting(false));
    }

  };

     const handleDeleteUpdate = (id:number) => {

    setSubmitting(true);
    if(isNonMotor){
      deleteNonMotorPolicyUpdate(policyId, id)
      .then((response: IRequestResponse) => {
        if (response) {
          onNonMotorPolicyUpdated?.(new NonAutoPolicy(response?.data));
          setReply('');
        }
      })
      .finally(() => setSubmitting(false));
    }

        if(!isNonMotor){
      deletePolicyUpdate(policyId, id)
      .then((response: IRequestResponse) => {
        if (response) {
          onPolicyUpdated?.(new Policy(response?.data));
          setReply('');
        }
      })
      .finally(() => setSubmitting(false));
    }
    
  };

    const handleEditNote = (update: IPolicyUpdate) => {
    setReply(update.description);
    setNoteCategory(update.category);
    setPriority(update.priority)
    setEditingNoteId(update.id);
  };

  const scrollToTop = () => {
    if (topSection.current) {
      topSection.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Animate play={show} start={startStyles} end={endStyles}>
      <div ref={containerRef} className="bg-white border border-primary-main px-14 pl-18 sm:pl-14 py-4 shadow-full overflow-y-auto h-[calc(100vh-100px)]">
        <div className=" flex justify-end pl-[1rem] items-center" ref={topSection}>
          <XMarkIcon onClick={onClose} className="cursor-pointer size-5" />
        </div>

        <h3 className="text-[24px] font-semibold py-2">
          {updates.length > 0 ? 'These are updates on this policy' : 'Add an update'}
        </h3>

        {/* {updates.map((update: IPolicyUpdate, index: number) => (
          <>
            <div
              key={index}
              className="min-h-[121px] border border-primary-main rounded p-6 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">
                  Policy {getPolicyReviewStatus(update.reviewStage ?? '')}
                </h3>

                <div className="flex items-center text-[10px] space-x-8 text-[#616161]">
                  <span>{update.staff}</span>
                  <span>{`${moment(update.date)?.format('DD/MM/YY')} | ${moment(
                    update.date
                  )?.format('HH:mm a')}`}</span>
                </div>
              </div>

              <div>{update.description}</div>

              <div className="flex items-center">
                <span
                  className={clsx(
                    'flex bg-primary-main rounded-full h-[16px] w-[16px] items-center justify-center mr-2',
                    { ['bg-[#DADADA]']: update.replies && update.replies?.length === 0 }
                  )}>
                  <ReplyIcon />
                </span>
                {update.replies && update?.replies?.length > 0 ? (
                  <>
                    <span>
                      {update.replies?.length}{' '}
                      {`${update.replies?.length === 1 ? 'reply' : 'replies'}`}
                    </span>
                  </>
                ) : (
                  <span className="text-[10px] text-[#616161]">Reply this update</span>
                )}
              </div>
            </div>
            <div className="px-8">
              <div
                className={clsx('border-l-[1px] border-primary-main', {
                  ['min-h-[1.5rem]']: index + 1 !== updates.length
                })}></div>
            </div>
          </>
        ))}

        {!user.isInsurerAgent() && (
          <div className="py-4 justify-between flex items-end space-x-4 " ref={sectionRef}>
            <div className="space-y-4 w-full">
              <TextArea
                name={'reason'}
                value={reply}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setReply(event.target.value);
                }}
                placeholder="Write an update..."
                rows={5}
              />
            </div>

            <div className=" flex justify-end space-x-6 relative">
              <div
                className=" rounded-full border border-primary-main absolute bottom-20 z-50 cursor-pointer p-2"
                onClick={scrollToTop}>
                <ArrowUpIcon className="h-5 w-5 " />
              </div>
              <Button
                label="Send"
                className="bg-primary-main w-fit px-8 py-2 h-[45px] rounded-[5px] text-base"
                onClick={handleSubmit}
                loading={submitting}
                disabled={submitting}
              />
            </div>
          </div>
        )} */}
            {show &&(
        <>
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40 transition-opacity duration-300 "
            // onClick={() => setShowNotesPanel(false)}
          />

          {/* Slide-in Panel */}
          <div className="fixed inset-y-0 right-0 w-full md:w-[600px] lg:w-[700px] bg-white dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col">
            {/* Panel Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-5 flex items-center justify-between shadow-lg">
              <div className="flex items-center">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                  <StickyNote className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Policy Notes</h2>
                  <p className="text-gray-300 text-sm">POL-{policyId}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>

            {/* Search and Stats */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center">
                  <StickyNote className="h-4 w-4 text-gray-700 mr-1.5 dark:text-darkBg-mainText" />
                  {/* <span className="font-medium text-gray-700">{filteredNotes.length}</span> */}
                  <span className="font-medium text-gray-700 dark:text-darkBg-mainText">{updates?.length || 0}</span>
                  <span className="text-gray-500 ml-1 dark:text-darkBg-mainText">notes</span>
                </div>
                <div className="flex items-center">
                  <Pin className="h-4 w-4 text-yellow-600 mr-1.5" />
                  {/* <span className="font-medium text-gray-700">{filteredNotes.filter(n => n.isPinned).length}</span> */}
                  <span className="font-medium text-gray-700 dark:text-darkBg-mainText">{totalPinnedUpdates}</span>
                  <span className="text-gray-500 ml-1 dark:text-darkBg-mainText">pinned</span>
                </div>
              </div>
            </div>

            {/* Note Editor Section */}
            <div className="px-6 py-4 bg-white border-b border-gray-200 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-darkBg-mainText">
                    {editingNoteId ? 'Edit Note' : 'Add New Note'}
                  </label>
                  {editingNoteId && (
                    <button
                      onClick={() => {
                        setEditingNoteId(null);
                        setReply('')
                        setPriority(null)
                        setNoteCategory(null);
                        setNoteTags([]);
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel Edit
                    </button>
                  )}
                </div>
                
                <TextArea
                  name={'reason'}
                  value={reply}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setReply(event.target.value);
                  }}
                  placeholder="Write an update..."
                  rows={5}
                />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <SelectDropdown
                      label="Category"
                      name="idType"
                      listSelectedValue={noteCategory}
                      onChange={(val: string) => {
                        setNoteCategory(val)

                      }}
                      list={CATEGORIES.map((cat:string)=>({
                        name: String(removeUnderscoresAndCapitalize(cat)),
                        value: cat
                      }))}
                      placeholder="Select a category"
                    />
                  </div>

                  <div>
                    <SelectDropdown
                      label="Priority"
                      name="priority"
                      listSelectedValue={priority}
                      onChange={(val: string) => {
                        setPriority(val)

                      }}
                      list={PRIORITIES.map((cat:string)=>({
                        name: String(removeUnderscoresAndCapitalize(cat)),
                        value: cat
                      }))}
                      placeholder="Select a priority"
                    />
                  </div>
                </div>

                {(
                  <div className="flex flex-wrap gap-2">
                    {noteTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2.5 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full"
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                        <button
                          // onClick={() => handleRemoveTag(tag)}
                          className="ml-1.5 hover:text-gray-900"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              
                <Button
                  label={editingNoteId ? 'Update Note' : 'Save Note'}
                  className="w-full py-2.5 !bg-gray-800 text-white dark:text-darkBg-mainText dark:hover:!bg-gray-400 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center font-medium"
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Notes List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {updates.length === 0 ? (
                <div className="text-center py-12">
                  <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <StickyNote className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium mb-1">No notes found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredNotes.map((update: IPolicyUpdate, index: number) => (
                    <div
                      key={index}
                      className={`bg-white border-2 rounded-lg p-4 transition-all duration-200 hover:shadow-md ${
                        update.pin
                          ? 'border-yellow-300 bg-yellow-50'
                          : 'border-gray-200 hover:border-gray-400'
                      } 
                      ${editingNoteId === update.id ? 'ring-2 ring-gray-500' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2 flex-1">
                          {update.pin && (
                            <Pin className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                          )}
                          {update.category && <span className={`inline-block px-2.5 rounded-full text-xs font-medium border ${getCategoryColor(update.category)}`}>
                            {removeUnderscoresAndCapitalize(update.category)}
                          </span>}
                           {update.priority && <span className={`inline-block px-2.5  rounded-full text-xs font-medium border ${getPriorityColor(update.priority)}`}>
                            {removeUnderscoresAndCapitalize(update.priority === 'NORMAL' ? 'MID' : update.priority )}
                          </span>}
                        </div>
                        {user?.fullName === update.staff && <div className="relative">
                          <button
                            onClick={() => setActiveNoteMenu(activeNoteMenu === update.id ? null : update.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
                          >
                            <MoreVertical className="h-4 w-4 text-gray-500" />
                          </button>
                          {activeNoteMenu === update.id && (
                            <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              <button
                                onClick={() =>  handleUpdatePin(Number(update.id), update, !update.pin)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                <Pin className="h-4 w-4 mr-2 text-gray-500" />
                                {update.pin ? 'Unpin' : 'Pin Note'}
                              </button>
                              <button
                                onClick={() => {
                                  handleEditNote(update);
                                  setActiveNoteMenu(null);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                <Edit2 className="h-4 w-4 mr-2 text-gray-500" />
                                Edit Note
                              </button>
                              <button
                                onClick={() =>  handleDeleteUpdate(Number(update.id))}
                                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>}
                      </div>

                      <p className="text-gray-800 text-sm mb-3 whitespace-pre-wrap leading-relaxed">
                        {update.description}
                      </p>

                      {/* {note.tags && note.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {note.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              <Tag className="h-3 w-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )} */}

                      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-200">
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {update.staff}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          { moment(update.date).format('DD-MM-YYYY')} At  {moment(update.date).format('HH:mm:ss')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {/* Panel Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200  dark:bg-darkBg dark:border dark:border-darkBg-border">
              <div className="flex items-center justify-between text-sm text-gray-600 dark:!text-darkBg-mainText">
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1.5" />
                  Last updated: {new Date().toLocaleDateString()}
                </span>
                <span className="font-medium text-gray-700 dark:!text-darkBg-mainText">
                  {/* {filteredNotes.length} of {policy.notes.length} notes */}
                  notes
                </span>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </Animate>
  );
};

export default PolicyUpdates;
